<script setup lang="ts">
/*
 *
 * IMPORTS
 *
 * */

// PrimeVue
import { default as PButton } from 'primevue/button'
import { default as PTag } from 'primevue/tag'

// Vue, Vue Router etc
import type { RouteLocationRaw } from 'vue-router'

// Custom components and types
import { computed } from 'vue'
import CBaseHeader from '@/presentation/components/base/CBaseHeader.vue'
import { LearningStatus } from '@/core/domain/resources/enums/LearningStatus.ts'

const props = defineProps<{
  status?: LearningStatus.Values
  primaryButton: {
    label: string
    onClick: () => void
    icon?: string
    disabled?: boolean
  }
  secondaryButton?: {
    label: string
    onClick: () => void
    icon?: string
    disabled?: boolean
  }
  backTo?: {
    label: string
    route: RouteLocationRaw
  }
  title: string
  subtitle?: string
}>()

const tagColor = computed(() => {
  switch (props.status) {
    case LearningStatus.Values.DRAFT:
      return 'secondary'
    case LearningStatus.Values.ACTIVE:
      return 'success'
    case LearningStatus.Values.ARCHIVED:
      return 'danger'
    default:
      return 'warning'
  }
})
</script>

<template>
  <c-base-header :title="title" :display-breadcrumbs="false" has-widget>
    <template #breadcrumbs>
      <router-link
        v-if="backTo"
        :to="backTo.route"
        class="text-muted-color no-underline"
        data-testid="back-link"
        >&lt; {{ backTo.label }}</router-link
      >
    </template>

    <template #widgets>
      <div class="col-span-4 flex h-full items-center justify-end gap-4">
        <div class="flex items-center gap-2" v-if="status">
          <p class="text-muted-color font-semibold">État :</p>
          <p-tag
            :value="LearningStatus.label(status)"
            :severity="tagColor"
            data-testid="status-tag"
          />
        </div>
        <p-button
          v-if="secondaryButton"
          :label="secondaryButton.label"
          severity="secondary"
          size="small"
          :icon="secondaryButton.icon"
          outlined
          class="whitespace-nowrap"
          @click="secondaryButton.onClick"
          :disabled="secondaryButton.disabled"
          data-testid="secondary-button"
        />
        <p-button
          :label="primaryButton.label"
          :icon="primaryButton.icon"
          severity="primary"
          size="small"
          class="whitespace-nowrap"
          @click="primaryButton.onClick"
          :disabled="primaryButton.disabled"
          data-testid="primary-button"
        />
      </div>
    </template>
  </c-base-header>
</template>
